import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MoveRight, FileText, MessageCircle, Mail, Star, Phone, CheckCircle, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhitepaperCTA } from "../WhitepaperCTA";
import { useTranslation } from "../../translations";

// Floating icon component
const FloatingIcon = ({ icon: Icon, x, y, delay, duration }) => {
  return (
    <motion.div
      className="absolute text-white/80 backdrop-blur-sm bg-white/10 rounded-full p-2 shadow-lg"
      style={{ x, y }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ 
        opacity: [0, 1, 1, 0],
        scale: [0.5, 1.1, 1, 0.5],
      }}
      transition={{ 
        duration: duration,
        delay: delay,
        repeat: Infinity,
        repeatDelay: 5,
      }}
    >
      <Icon size={20} />
    </motion.div>
  );
};

function Hero() {
  const { t, lang } = useTranslation();
  const [titleNumber, setTitleNumber] = useState(0);
  const titles = useMemo(
    () => t.heroAnimated.rotatingTitles,
    [t.heroAnimated.rotatingTitles]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleNumber === titles.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles]);

  // Define floating icons with their positions and animation timings
  const floatingIcons = [
    { icon: MessageCircle, x: "10%", y: "20%", delay: 0, duration: 4 },
    { icon: Star, x: "85%", y: "25%", delay: 1.5, duration: 4.5 },
    { icon: Mail, x: "75%", y: "65%", delay: 2.7, duration: 5 },
    { icon: CheckCircle, x: "15%", y: "70%", delay: 3.2, duration: 4.2 },
    { icon: Phone, x: "20%", y: "40%", delay: 4.5, duration: 4.8 },
    { icon: Users, x: "80%", y: "45%", delay: 5.8, duration: 4.3 },
    { icon: Calendar, x: "50%", y: "15%", delay: 7, duration: 4.6 },
  ];

  return (
    <div className="w-full relative overflow-hidden">
      {/* Gradient background with radial glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2A3F5F] via-[#1e2d45] to-[#3C5679] z-0">
        {/* Center glow effect */}
        <div className="absolute inset-0 bg-radial-gradient opacity-30"></div>
      </div>
      
      {/* Floating icons */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        {floatingIcons.map((icon, index) => (
          <FloatingIcon
            key={index}
            icon={icon.icon}
            x={icon.x}
            y={icon.y}
            delay={icon.delay}
            duration={icon.duration}
          />
        ))}
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex gap-8 pt-32 pb-24 md:pt-40 md:pb-32 lg:pt-48 lg:pb-40 items-center justify-center flex-col min-h-[90vh]">
          <div className="flex gap-4 flex-col">
            <h1 className="text-4xl sm:text-5xl md:text-6xl max-w-3xl tracking-tighter text-center font-regular">
              <span className="text-white">{t.heroAnimated.title}</span>
              <span className="relative flex w-full justify-center overflow-hidden text-center md:pb-4 md:pt-1">
                &nbsp;
                {titles.map((title, index) => (
                  <motion.span
                    key={index}
                    className="absolute font-semibold text-jaimy-red"
                    initial={{ opacity: 0, y: "-100" }}
                    transition={{ type: "spring", stiffness: 50 }}
                    animate={
                      titleNumber === index
                        ? {
                            y: 0,
                            opacity: 1,
                          }
                        : {
                            y: titleNumber > index ? -150 : 150,
                            opacity: 0,
                          }
                    }
                  >
                    {title}
                  </motion.span>
                ))}
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl leading-relaxed tracking-tight text-gray-200 max-w-2xl text-center mx-auto mt-4">
              {t.heroAnimated.subtitle.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i < t.heroAnimated.subtitle.split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mt-6 w-full max-w-md">
            <WhitepaperCTA className="w-full sm:w-1/2" />
            <Button 
              size="lg" 
              className="w-full sm:w-1/2 gap-2 gradient-bg hover:opacity-90 text-white border-0 rounded-full px-6 py-3 h-auto"
              onClick={() => {
                // Use a custom event to trigger the demo form
                const event = new CustomEvent('openDemoForm');
                window.dispatchEvent(event);
              }}
            >
              {t.heroAnimated.demoButton}
              <MoveRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Hero };